import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import InfoTooltip from "../components/common/InfoTooltip";
import { useAuth } from "../context/AuthContext";
import { getBillingSummary } from "../api/billing";
import { formatInTimeZone } from "../utils/datetime";

const MODAL_TEXT = "text-sm md:text-base";

const formatDateTime = (value, timeZone) =>
  formatInTimeZone(
    value,
    timeZone,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    },
    "en-CA"
  );

const formatStatusLabel = (value) => {
  const normalized = (value || "").toLowerCase();
  if (!normalized || normalized === "no_card_on_file") return "No card on file";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export default function BillingCardOnFile() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getBillingSummary(token);
        if (!isActive) return;
        setSummary(data);
      } catch (err) {
        if (!isActive) return;
        setSummary(null);
        setError(err?.response?.data?.detail || "Unable to load card details.");
      } finally {
        if (isActive) setLoading(false);
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [token]);

  const timeZone = useMemo(
    () => summary?.salon_timezone || user?.salon_timezone || "America/Toronto",
    [summary?.salon_timezone, user?.salon_timezone]
  );

  const hasCard = Boolean(summary?.card?.last4);

  return (
    <div className="manageSectionShell">
      <div
        className={`manageSectionWide manage-section-wide manage-section-compact ${MODAL_TEXT}`}
      >
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">Card on File</h1>
          </div>

          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Saved Card Details
                  </h2>
                  <p className="text-sm text-slate-500">
                    View the current default card used for this salon's subscription billing.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/manager/manage/billing")}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/manager/manage/billing/replace-card")}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    Replace this Card
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-sm text-slate-500">Loading card details...</div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Brand
                      <InfoTooltip
                        text="Card brand used for recurring subscription charges."
                        label="Brand"
                      />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {hasCard ? summary?.card?.brand || "Card" : "No card on file"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Last 4
                      <InfoTooltip text="Last 4 digits of the saved card." label="Last 4" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {hasCard ? `•••• ${summary?.card?.last4}` : "--"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Expiry
                      <InfoTooltip text="Expiration month and year of the saved card." label="Expiry" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {hasCard
                        ? `${String(summary?.card?.exp_month || "").padStart(2, "0")}/${summary?.card?.exp_year || ""}`
                        : "--"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Cardholder
                      <InfoTooltip text="Cardholder name when available." label="Cardholder" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {summary?.card?.cardholder_name || "Not available"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Subscription Status
                      <InfoTooltip text="Current subscription status for this salon." label="Subscription Status" />
                    </div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {formatStatusLabel(summary?.status)}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {summary?.next_billing_utc
                        ? `Next billing: ${formatDateTime(summary.next_billing_utc, timeZone)}`
                        : "No next billing date"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
